// import { Rectangle } from 'pixi.js';

import { Rectangle } from 'pixi.js';

import { getSceneStartEndTimes } from 'js/playback/helpers/timings';
import { AnimationTimingOptions, PlaybackScribeModel } from 'js/types';
import { mapSceneElementIdsToElements } from 'js/shared/helpers/scenesHelpers';
import { getElementTimings } from 'js/playback/helpers/getElementTimings';
import { renderer } from 'js/config/defaults';
import { getCameraPositionAtTime } from 'js/playback/pixi-player/helpers/getCameraPositionAtTime';
import { toSeconds } from 'js/editor/PlayerContext/timingHelpers';

import { getAnimatingScenes } from './scenesAnimating';
import { stripElementForRenderer } from './stripElementForRenderer';
import { getViewportData } from './getViewportData';
import { getElementBoundingRectangle } from './getElementBoundingRectangle';

interface StripProjectOfElementsNotInSegmentCameraViewsConfig {
  project: PlaybackScribeModel;
  startTimeMs: number;
  endTimeMs: number;
}

export function stripProjectOfElementsNotInSegmentCameraViews({
  project,
  startTimeMs,
  endTimeMs
}: StripProjectOfElementsNotInSegmentCameraViewsConfig) {
  const durationMs = endTimeMs - startTimeMs;
  const numberFrames = (durationMs / 1000) * renderer.frameRate;
  const frameSize = durationMs / numberFrames;
  const frames = Array(numberFrames)
    .fill(null)
    .map((_, frameIndex) => ({ timestamp: startTimeMs + frameIndex * frameSize }));
  const canvasDimensions = getViewportData(project.canvasSize);
  const animatingScenes = getAnimatingScenes(project, project.scenes, startTimeMs, endTimeMs);

  const sceneElementsInOrderWithTimingsAndCameraRectangles = animatingScenes.map(scene => {
    const elements = mapSceneElementIdsToElements(project, scene);
    const sceneTimings = getSceneStartEndTimes(project, scene);
    const sceneCameras = elements.filter(el => el.type === 'Camera');
    const cameraRectangles = frames.map(({ timestamp }) => {
      const position = getCameraPositionAtTime(
        sceneCameras,
        toSeconds(timestamp - sceneTimings.startTime),
        project,
        toSeconds(sceneTimings.startTime)
      );
      return new Rectangle(
        position.x,
        position.y,
        canvasDimensions.width / position.scale,
        canvasDimensions.height / position.scale
      );
    });

    let initialCameraTimings: AnimationTimingOptions;
    return elements.map((element, elementIndex, elements) => {
      const timings = getElementTimings(
        elements,
        element,
        project,
        scene,
        sceneTimings.startTime,
        initialCameraTimings
      );

      if (elementIndex === 0) {
        initialCameraTimings = timings;
      }

      return {
        element,
        timings,
        cameraRectangles
      };
    });
  });

  // First strip any elements that start time is after the end time of the segment
  sceneElementsInOrderWithTimingsAndCameraRectangles.flat().forEach(elementAndTimings => {
    const animatingScenes = getAnimatingScenes(project, project.scenes, startTimeMs, endTimeMs);
    let timingsOffset = 0;
    if (animatingScenes.length) {
      // Ensure the scene transition times for all animating scenes are taking into account
      animatingScenes.forEach(scene => {
        timingsOffset += scene.settings.sceneTransitionTime ?? 0;
      });
    }
    if (
      elementAndTimings.timings.startTimeMs - timingsOffset * 1000 > endTimeMs &&
      elementAndTimings.element.type !== 'Camera'
    ) {
      stripElementForRenderer(elementAndTimings.element);
    }
  });

  // Calculate the view rectangle for each frame of this segment -
  // and strip any element that don't fall inside any of the camera rectangles.
  sceneElementsInOrderWithTimingsAndCameraRectangles.forEach(elementsAndTimings => {
    elementsAndTimings.forEach(elAndTimings => {
      if (elAndTimings.element.type === 'Camera') return;

      const elementBoundingBox = getElementBoundingRectangle(elAndTimings.element, canvasDimensions);
      const visibleInAFrame = elAndTimings.cameraRectangles.some(cameraBoundingBox =>
        cameraBoundingBox.intersects(elementBoundingBox)
      );

      // If element is outside all of the camera rectangles then strip it out
      if (!visibleInAFrame) {
        stripElementForRenderer(elAndTimings.element);
      }
    });
  });

  return project;
}
